import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { confirmWithCost } from "@/lib/cost";
import { api, errMsg, fmtDate } from "@/lib/api";

const hashOf = async (s) => {
  const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(s || ""));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
};

const Claim = ({ c, onAct, busy }) => {
  const bad = c.status === "incorrect";
  return (
    <li className={`border-l-2 pl-4 py-2 ${bad ? "border-[#9B2C2C]" : "border-[#B7791F]"}`} data-testid={`fact-claim-${c.id}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: bad ? "#9B2C2C" : "#B7791F" }}>{bad ? "Looks incorrect" : "Couldn't verify"}</div>
      <p className="italic mt-1">“{c.quote}”</p>
      {c.finding && <p className="text-sm text-[#5C5255] mt-1">{c.finding}</p>}
      {c.correction && <p className="text-sm mt-1"><span className="font-semibold">Suggested:</span> {c.correction}</p>}
      <div className="flex flex-wrap gap-3 mt-2 text-xs">
        {c.correction && <button disabled={busy} onClick={() => onAct(c, "apply")} className="underline text-[#2D5A3F]" data-testid={`fact-apply-${c.id}`}>Apply correction</button>}
        <button disabled={busy} onClick={() => onAct(c, "remove")} className="underline text-[#9B2C2C]" data-testid={`fact-remove-${c.id}`}>Remove the sentence</button>
        <button disabled={busy} onClick={() => onAct(c, "keep")} className="underline text-[#5C5255]" data-testid={`fact-keep-${c.id}`}>It's correct — keep</button>
      </div>
    </li>
  );
};

export const FactCheckTab = ({ p, theme, ensureSaved, onStarted, onPatch, dirty }) => {
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(null);
  const fc = p.fact_check?.sections || {};
  const job = p.fact_job || {};
  const running = job.status === "running";
  const secs = (p.sections || []).filter((s) => fc[s.id]);
  const all = secs.flatMap((s) => fc[s.id].claims.map((c) => ({ ...c, sid: s.id })));
  const open = all.filter((c) => c.status !== "verified" && !c.resolution);
  const verified = all.filter((c) => c.status === "verified").length;

  useEffect(() => {
    Promise.all((p.sections || []).map(async (s) => (fc[s.id] && fc[s.id].hash !== (await hashOf(s.body)) ? 1 : 0)))
      .then((x) => setStale(x.reduce((a, b) => a + b, 0)));
  }, [p.sections, fc]);

  const run = async (onlyChanged) => {
    if (!(await confirmWithCost(p.id, { kind: "fact_check", only_changed: onlyChanged }, onlyChanged ? "Check every new or changed chapter against the web?" : "Re-check every chapter from scratch?"))) return;
    setBusy(true);
    try {
      await ensureSaved();
      const r = await api.post(`/products/${p.id}/fact-check`, { only_changed: onlyChanged });
      onPatch({ fact_job: r.data });
      onStarted();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const act = async (c, action) => {
    if (dirty) return toast.error("Save your changes first");
    if (action === "remove" && !window.confirm("Remove the whole sentence containing this claim?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/products/${p.id}/fact-check/claims/${c.id}`, { action });
      onPatch({ sections: r.data.sections, fact_check: r.data.fact_check });
      toast.success(action === "keep" ? "Marked as correct" : action === "apply" ? "Correction applied" : "Sentence removed");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fact-check-tab">
      <div className="bg-[#FFFDF9] border border-[#2A2325]/10 p-6 flex flex-wrap items-center gap-6">
        <SearchCheck size={28} style={{ color: theme.dark }} />
        <div className="flex-1 min-w-[240px]">
          <div className="eyebrow">Fact check · web search via OpenRouter</div>
          <p className="text-sm text-[#5C5255] mt-1">Every name, date, figure and statement is checked against the web. Sources are listed on a Sources page in the PDF and HTML. Anything doubtful waits here for you.</p>
          {p.fact_check?.checked_at && <p className="text-xs text-[#5C5255] mt-1" data-testid="fact-checked-at">Last checked {fmtDate(p.fact_check.checked_at)}{stale ? ` · ${stale} chapter${stale > 1 ? "s" : ""} changed since` : ""}</p>}
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={() => run(true)} disabled={running || busy} className="rounded-none gap-2" style={{ background: theme.dark }} data-testid="fact-check-run-btn">
            {running && <Loader2 size={14} className="animate-spin" />}{running ? `Checking ${job.done || 0}/${job.total}` : "Check new & changed chapters"}
          </Button>
          {secs.length > 0 && !running && <button onClick={() => run(false)} className="text-xs underline text-[#5C5255]" data-testid="fact-check-all-btn">Re-check everything</button>}
        </div>
      </div>
      {job.failed?.length > 0 && !running && <p className="text-sm text-[#9B2C2C]" data-testid="fact-check-failed">Couldn't check: {job.failed.join(", ")}. Run the check again.</p>}
      {secs.length > 0 && (
        <div className="flex gap-6 text-sm" data-testid="fact-check-summary">
          <span className="flex items-center gap-1.5 text-[#2D5A3F]"><CheckCircle2 size={15} /> {verified} verified</span>
          <span className="flex items-center gap-1.5" style={{ color: open.length ? "#9B2C2C" : "#5C5255" }}><AlertTriangle size={15} /> {open.length} need review</span>
          <span className="text-[#5C5255]">{secs.length} of {p.sections?.length || 0} chapters checked</span>
        </div>
      )}
      {secs.map((s) => {
        const r = fc[s.id];
        const flagged = r.claims.filter((c) => c.status !== "verified" && !c.resolution);
        return (
          <div key={s.id} className="bg-[#FFFDF9] border border-[#2A2325]/10 p-6" data-testid={`fact-section-${s.id}`}>
            <div className="flex justify-between gap-4">
              <h3 className="font-playfair text-xl">{s.heading}</h3>
              <span className="text-xs text-[#5C5255] shrink-0">{r.claims.length} claims · {r.sources.length} sources</span>
            </div>
            {flagged.length > 0 ? (
              <ul className="mt-4 space-y-3">{flagged.map((c) => <Claim key={c.id} c={c} onAct={act} busy={busy} />)}</ul>
            ) : (
              <p className="text-sm text-[#2D5A3F] mt-2">All claims verified or reviewed.</p>
            )}
            {r.sources.length > 0 && (
              <details className="mt-4 text-sm">
                <summary className="cursor-pointer text-[#5C5255]">Sources</summary>
                <ol className="list-decimal pl-5 mt-2 space-y-1">
                  {r.sources.map((x) => <li key={x.url}><a href={x.url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1 break-all">{x.title} <ExternalLink size={11} /></a></li>)}
                </ol>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
};
